import { isBedbankItem, isExperienceItem, isLEHotelItem } from 'lib/checkout/checkoutUtils'
import type {
  TripChatAccommodationCartCandidate,
  TripChatCartCandidate,
  TripChatExperienceCartCandidate,
} from './tripChatCartCandidateUtils'
import {
  isCartPanelExperienceDateRequired,
  isCartPanelExperienceTimeRequired,
  type CartPanelExperienceTicket,
} from './cartPanelUtils'

/** The fields svc-trip's reconciliation builds a linked row from; see feature-notes. */
export interface TripChatAccommodationCartSelection {
  productType: 'accommodation'
  cartItemId: string | undefined
  packageId: string | undefined
  roomRateId: string | undefined
  duration: number | undefined
  occupancies?: Array<App.Occupants>
}

export interface TripChatExperienceCartSelection {
  productType: 'experience'
  cartItemId: string | undefined
  experienceId: string
  tickets: Array<CartPanelExperienceTicket>
  bookingType: string | undefined
  date: string | undefined
  time: string | undefined
  pickupPointId: string | undefined
  redemptionLocationId: string | undefined
}

export type TripChatCartSelection = TripChatAccommodationCartSelection | TripChatExperienceCartSelection

export function toTripChatAccommodationCartSelection(
  candidate: Pick<TripChatAccommodationCartCandidate, 'cartItemId' | 'packageId' | 'roomRateId' | 'duration' | 'occupancies'>,
): TripChatAccommodationCartSelection {
  return {
    productType: 'accommodation',
    cartItemId: candidate.cartItemId,
    packageId: candidate.packageId,
    roomRateId: candidate.roomRateId,
    duration: candidate.duration,
    occupancies: candidate.occupancies,
  }
}

export function toTripChatExperienceCartSelection(
  candidate: Pick<
    TripChatExperienceCartCandidate,
    'cartItemId' | 'experienceId' | 'tickets' | 'bookingType' | 'date' | 'time' | 'pickupPointId' | 'redemptionLocationId'
  >,
): TripChatExperienceCartSelection {
  return {
    productType: 'experience',
    cartItemId: candidate.cartItemId,
    experienceId: candidate.experienceId,
    tickets: candidate.tickets,
    bookingType: candidate.bookingType,
    date: candidate.date,
    time: candidate.time,
    pickupPointId: candidate.pickupPointId,
    redemptionLocationId: candidate.redemptionLocationId,
  }
}

export function toTripChatCartSelection(candidate: TripChatCartCandidate): TripChatCartSelection {
  return candidate.productType === 'accommodation'
    ? toTripChatAccommodationCartSelection(candidate)
    : toTripChatExperienceCartSelection(candidate)
}

type CartSelectionField =
  | 'packageId'
  | 'roomRateId'
  | 'duration'
  | 'occupancy'
  | 'experienceId'
  | 'tickets'
  | 'date'
  | 'time'
  | 'pickupPointId'
  | 'redemptionLocationId'

export type TripChatCartItemMatch =
  | { status: 'linked', cartItem: App.MultiCartItem }
  | { status: 'missing' }
  | { status: 'differs', cartItem: App.MultiCartItem, fields: Array<CartSelectionField> }

export function matchTripChatCartItem(
  cartItemsMap: Map<string, App.MultiCartItem>,
  item: TripChatCartSelection,
): TripChatCartItemMatch {
  const cartItem = getLinkedCartRow(cartItemsMap, item)
  if (!cartItem) return { status: 'missing' }
  const fields = findCartSelectionDifferences(item, cartItem.item)
  if (!fields) return { status: 'missing' }
  return fields.length > 0 ? { status: 'differs', cartItem, fields } : { status: 'linked', cartItem }
}

/** Keyed by row modification time, so drift after a repair retriggers but a deferred repair doesn't. */
export function toUnmatchedCartRowSignatures(
  candidates: Array<TripChatCartCandidate>,
  cartItemsMap: Map<string, App.MultiCartItem>,
): Array<string> {
  return candidates.flatMap((item) => {
    if (!item.cartItemId) return []
    const match = matchTripChatCartItem(cartItemsMap, toTripChatCartSelection(item))
    if (match.status === 'linked') return []
    const rowVersion = match.status === 'differs' ? match.cartItem.lastModifiedAt : 'missing'
    return [`${item.tripItemId}:${item.cartItemId}:${rowVersion}`]
  })
}

function getLinkedCartRow(
  cartItemsMap: Map<string, App.MultiCartItem>,
  item: Pick<TripChatCartSelection, 'cartItemId'>,
): App.MultiCartItem | undefined {
  return item.cartItemId ? cartItemsMap.get(item.cartItemId) : undefined
}

/** Undefined when the row is not the kind of product the itinerary item is. */
function findCartSelectionDifferences(
  selection: TripChatCartSelection,
  cartItem: App.Checkout.AnyItem,
): Array<CartSelectionField> | undefined {
  if (selection.productType === 'experience') {
    if (!isExperienceItem(cartItem)) return undefined
    // svc-trip books the live offer's booking type, so the row, not the saved type, says whether a date applies.
    const isDateRequired = isCartPanelExperienceDateRequired(selection.bookingType, cartItem.isBuyNowBookLater, cartItem.isGift)
    const isTimeRequired = isCartPanelExperienceTimeRequired(selection.bookingType, cartItem.isBuyNowBookLater, cartItem.isGift)
    return differingFields([
      ['experienceId', selection.experienceId, cartItem.experienceId],
      ['tickets', toTicketsKey(selection.tickets), toTicketsKey(cartItem.tickets)],
      isDateRequired ? ['date', toDay(selection.date), toDay(cartItem.date)] : undefined,
      isTimeRequired ? ['time', toTime(selection.time), toTime(cartItem.time)] : undefined,
      selection.pickupPointId === undefined ? undefined : ['pickupPointId', selection.pickupPointId, cartItem.pickupPointId],
      selection.redemptionLocationId === undefined
        ? undefined
        : ['redemptionLocationId', selection.redemptionLocationId, cartItem.redemptionLocationId],
    ])
  }

  const hotel = toComparableHotel(cartItem)
  if (!hotel) return undefined
  const savedOccupancy = selection.occupancies?.[0]

  return differingFields([
    ['packageId', selection.packageId, hotel.packageId],
    ['roomRateId', selection.roomRateId, hotel.roomRateId],
    ['duration', selection.duration, hotel.duration],
    savedOccupancy === undefined
      ? undefined
      : ['occupancy', toOccupancyKey(savedOccupancy), hotel.occupancy && toOccupancyKey(hotel.occupancy)],
  ])
}

interface ComparableHotel {
  packageId: string
  roomRateId: string
  duration: number
  occupancy: App.Occupants | undefined
}

/** Only the hotel rows Trip Chat builds; svc-trip leaves any other row as it is. */
function toComparableHotel(cartItem: App.Checkout.AnyItem): ComparableHotel | undefined {
  if (isBedbankItem(cartItem)) {
    return { packageId: cartItem.roomId, roomRateId: cartItem.roomRateId, duration: cartItem.duration, occupancy: cartItem.occupancy }
  }
  if (!isLEHotelItem(cartItem)) return undefined
  return { packageId: cartItem.packageId, roomRateId: cartItem.roomRateId, duration: cartItem.duration, occupancy: cartItem.occupancy }
}

type ComparedPair = [CartSelectionField, string | number | undefined, string | number | undefined]

// An unset expected value differs too: reconciliation removes a row the itinerary can no longer book.
function differingFields(pairs: Array<ComparedPair | undefined>): Array<CartSelectionField> {
  return pairs.flatMap(pair => (
    pair && (pair[1] === undefined || pair[1] !== pair[2]) ? [pair[0]] : []
  ))
}

const toDay = (value: string | undefined): string | undefined => value?.slice(0, 10)
const toTime = (value: string | undefined): string | undefined => value?.slice(0, 5)

// Mirrors svc-trip's occupancyComparisonKey: a missing children count is implied by the child ages.
function toOccupancyKey(occupancy: App.Occupants): string {
  const childrenAge = [...occupancy.childrenAge ?? []].sort((a, b) => a - b)
  return [
    occupancy.adults,
    occupancy.children ?? childrenAge.length,
    occupancy.infants ?? 0,
    occupancy.teenagers ?? 0,
    childrenAge.join(','),
  ].join('|')
}

function toTicketsKey(tickets: Array<{ ticketId: string, count: number }>): string {
  return tickets.map(ticket => `${ticket.ticketId}:${ticket.count}`).sort().join(',')
}
